import { isTimeslotAction, taskGoalIds, taskLifeAreaIds } from '@griply/utils';
import { ID } from 'shared/types/id';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';
import { UpcomingCalendarViewOptions } from 'shared/types/upcoming-calendar-view-options';

export type Options = {
  hiddenGoalIds: Set<ID>;
  lifeAreaLinkedGoalIds?: ID[];
};

type LifeAreaLookup = {
  hiddenLifeAreaIds: Set<ID>;
  hiddenGoalIds: Set<ID>;
  linkedGoalIds: Set<ID>;
};

const isHiddenByLifeArea = (
  task: Task,
  { hiddenLifeAreaIds, hiddenGoalIds, linkedGoalIds }: LifeAreaLookup,
): boolean => {
  const lifeAreaIds = taskLifeAreaIds(task);
  if (lifeAreaIds.length) {
    // One visible life area is enough to keep the task on the calendar.
    return lifeAreaIds.every((lifeAreaId) => hiddenLifeAreaIds.has(lifeAreaId));
  }

  const goalIds = taskGoalIds(task);
  if (goalIds.length && goalIds.every((goalId) => hiddenGoalIds.has(goalId))) {
    return true;
  }

  if (goalIds.some((goalId) => linkedGoalIds.has(goalId))) {
    return false;
  }

  return hiddenLifeAreaIds.has(lifeAreaOtherID);
};

export const filterCalendarTasksByViewOptions = (
  tasks: Task[],
  viewOptions: UpcomingCalendarViewOptions,
  { hiddenGoalIds, lifeAreaLinkedGoalIds }: Options,
): Task[] => {
  const hiddenTaskTypes = new Set(viewOptions.hiddenTaskTypes);
  const hiddenLifeAreaIds = new Set(viewOptions.hiddenLifeAreaIds);
  const linkedGoalIds = new Set(lifeAreaLinkedGoalIds ?? []);

  return tasks.filter((task) => {
    if (hiddenTaskTypes.has(task.type)) {
      return false;
    }

    if (viewOptions.hideFocusBlocks && isTimeslotAction(task.action?.type)) {
      return false;
    }

    if (!hiddenLifeAreaIds.size) {
      return true;
    }

    return !isHiddenByLifeArea(task, {
      hiddenLifeAreaIds,
      hiddenGoalIds,
      linkedGoalIds,
    });
  });
};
